import React, { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, apiError } from "../lib/api";
import { toast } from "sonner";
import { FileText, Library, Upload, Check, RefreshCw, Clock } from "lucide-react";

const STAGES = ["READING DOCUMENT", "EXTRACTING TEXT", "CALIBRATING TASKS", "FINALIZING"];

// Extensions accepted by the backend's _extract_pd_text helper. Kept in
// sync with server.py — DOC uses `antiword` (declared in system_deps.txt).
const ACCEPT_EXT = ".pdf,.docx,.doc";
const ACCEPT_MIME = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/msword",
];

function extOf(name) {
  const m = /\.([a-z0-9]+)$/i.exec(name || "");
  return m ? m[1].toLowerCase() : "";
}

export default function ProfileNew() {
  const nav = useNavigate();
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [form, setForm] = useState({ employee_name: "", employee_email: "", employee_title: "", department: "" });
  const [busy, setBusy] = useState(false);
  const [stage, setStage] = useState(0);

  // Source picker: "upload" or "library"
  const [source, setSource] = useState("upload");
  const [savedPds, setSavedPds] = useState([]);
  const [pdsLoading, setPdsLoading] = useState(false);
  const [pdsLoaded, setPdsLoaded] = useState(false);
  const [pickedPdId, setPickedPdId] = useState(null);

  // Department autocomplete — pre-fetch existing org departments once.
  const [orgDepartments, setOrgDepartments] = useState([]);
  useEffect(() => {
    api.get("/profiles/departments")
      .then((r) => setOrgDepartments(Array.isArray(r.data) ? r.data : []))
      .catch(() => setOrgDepartments([]));
  }, []);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // Lazy-load the saved PD library the first time the user switches to it.
  // `pdsLoaded` gates the useEffect so an empty result (savedPds.length === 0)
  // doesn't cause the effect to re-fire forever — an easy trap when we only
  // relied on `savedPds.length` as the "done" signal.
  const loadSavedPds = React.useCallback(() => {
    setPdsLoading(true);
    api.get("/pd-generator/documents")
      .then((r) => setSavedPds(Array.isArray(r.data) ? r.data : []))
      .catch((err) => toast.error(apiError(err, "Failed to load saved PDs")))
      .finally(() => {
        setPdsLoading(false);
        setPdsLoaded(true);
      });
  }, []);

  useEffect(() => {
    if (source !== "library" || pdsLoaded || pdsLoading) return;
    loadSavedPds();
  }, [source, pdsLoaded, pdsLoading, loadSavedPds]);

  const onFilePick = (f) => {
    if (!f) { setFile(null); return; }
    const ext = extOf(f.name);
    if (!["pdf", "docx", "doc"].includes(ext)) {
      toast.error("Only PDF, DOCX, or DOC files are supported");
      return;
    }
    setFile(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (source === "upload" && !file) { toast.error("Select a PDF, DOCX, or DOC file"); return; }
    if (source === "library" && !pickedPdId) { toast.error("Pick a saved PD from the library"); return; }
    setBusy(true); setStage(0);
    const stageWalker = setInterval(() => setStage((s) => Math.min(s + 1, STAGES.length - 1)), 1200);
    try {
      const fd = new FormData();
      if (source === "upload") {
        fd.append("pdf", file);
      } else if (source === "library") {
        fd.append("pd_document_id", pickedPdId);
      }
      // source === "skip" → nothing appended; backend creates a shell profile.
      fd.append("employee_name", form.employee_name);
      fd.append("employee_email", form.employee_email);
      fd.append("employee_title", form.employee_title);
      fd.append("department", form.department);
      const r = await api.post("/profiles", fd, { headers: { "Content-Type": "multipart/form-data" } });
      clearInterval(stageWalker);
      setStage(STAGES.length - 1);
      if (source === "skip") {
        toast.success("Profile created — attach a PD to generate tasks");
      } else {
        toast.success(`Profile created · ${r.data.tasks.length} tasks extracted`);
      }
      nav(`/profiles/${r.data.profile_id}`);
    } catch (err) {
      clearInterval(stageWalker);
      toast.error(apiError(err, "Upload failed"));
      setBusy(false);
    }
  };

  const pickedPd = savedPds.find((d) => d.id === pickedPdId);

  return (
    <div className="max-w-3xl mx-auto px-6 py-12">
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="font-mono-cal text-[11px] uppercase tracking-[0.2em] text-[#666]">[NEW] Employee profile</div>
        <Link to="/pd-generator" className="cal-btn cal-btn-ghost" data-testid="pd-generator-btn">
          <FileText size={14} /> PD Generator
        </Link>
      </div>
      <h1 className="font-display font-bold text-5xl mb-2">Create employee profile</h1>
      <p className="text-[#666] mb-10">
        Upload a Position Description (PDF, DOCX, or DOC) or pick one from your saved PD library.
        We&apos;ll extract every measurable task and attach them to this profile.
        <span className="block mt-1 text-sm">
          Don&apos;t have a PD yet? Use the <Link to="/pd-generator" className="underline">PD Generator</Link> to draft one first.
        </span>
      </p>

      <form onSubmit={submit} className="cal-card p-8 space-y-6" data-testid="new-profile-form">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="cal-label">Employee full name</label>
            <input required value={form.employee_name} onChange={upd("employee_name")} className="cal-input" data-testid="input-emp-name" />
          </div>
          <div>
            <label className="cal-label">Employee email</label>
            <input required type="email" value={form.employee_email} onChange={upd("employee_email")} className="cal-input" data-testid="input-emp-email" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="cal-label">Job title (optional)</label>
            <input value={form.employee_title} onChange={upd("employee_title")} placeholder="Senior Product Manager" className="cal-input" data-testid="input-emp-title" />
          </div>
          <div>
            <label className="cal-label">Department (optional)</label>
            <input
              list="pd-departments"
              value={form.department}
              onChange={upd("department")}
              placeholder="Engineering, Sales, Ops…"
              className="cal-input"
              maxLength={140}
              data-testid="input-emp-department"
            />
            <datalist id="pd-departments">
              {orgDepartments.map((d) => <option key={d} value={d} />)}
            </datalist>
          </div>
        </div>

        {/* Source toggle */}
        <div>
          <label className="cal-label">Position Description source</label>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" role="tablist" aria-label="PD source">
            <button
              type="button"
              onClick={() => setSource("upload")}
              className={`p-3 border-2 text-left transition ${source === "upload" ? "border-[#FF4F00] bg-[#FFF5F0]" : "border-[#DDD] bg-white hover:border-[#111]"}`}
              data-testid="pd-source-upload"
              disabled={busy}
              role="tab"
              aria-selected={source === "upload"}
            >
              <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#666] mb-1 flex items-center gap-1">
                <Upload size={11} /> Upload file
              </div>
              <div className="font-display font-semibold text-sm">PDF, DOCX, or DOC</div>
            </button>
            <button
              type="button"
              onClick={() => setSource("library")}
              className={`p-3 border-2 text-left transition ${source === "library" ? "border-[#FF4F00] bg-[#FFF5F0]" : "border-[#DDD] bg-white hover:border-[#111]"}`}
              data-testid="pd-source-library"
              disabled={busy}
              role="tab"
              aria-selected={source === "library"}
            >
              <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#666] mb-1 flex items-center gap-1">
                <Library size={11} /> Saved library
              </div>
              <div className="font-display font-semibold text-sm">Pick a saved PD</div>
            </button>
            <button
              type="button"
              onClick={() => setSource("skip")}
              className={`p-3 border-2 text-left transition ${source === "skip" ? "border-[#FF4F00] bg-[#FFF5F0]" : "border-[#DDD] bg-white hover:border-[#111]"}`}
              data-testid="pd-source-skip"
              disabled={busy}
              role="tab"
              aria-selected={source === "skip"}
            >
              <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#666] mb-1 flex items-center gap-1">
                <Clock size={11} /> Skip for now
              </div>
              <div className="font-display font-semibold text-sm">Attach PD later</div>
            </button>
          </div>
        </div>

        {source === "skip" && (
          <div className="cal-card p-4 bg-[#F4F4F0] text-sm" data-testid="pd-source-skip-note">
            The profile will be created without tasks. You can attach a PD (upload or from your library) later from the profile page — that&apos;s when tasks are generated.
          </div>
        )}

        {/* Upload dropzone */}
        {source === "upload" && (
          <div>
            <div
              onClick={() => !busy && inputRef.current?.click()}
              className={`border border-dashed border-[#111] p-10 text-center cursor-pointer transition ${busy ? "pointer-events-none opacity-60" : "hover:bg-[#F4F4F0]"}`}
              data-testid="dropzone"
            >
              <input
                ref={inputRef}
                type="file"
                accept={`${ACCEPT_EXT},${ACCEPT_MIME.join(",")}`}
                onChange={(e) => onFilePick(e.target.files?.[0] || null)}
                className="hidden"
                data-testid="file-input"
              />
              <div className="font-mono-cal text-[11px] uppercase tracking-widest text-[#666] mb-2">
                [ PDF · DOCX · DOC ]
              </div>
              <div className="font-display font-bold text-xl">{file ? file.name : "Click to select a document"}</div>
              {file && <div className="mt-1 text-xs text-[#666]">{(file.size / 1024).toFixed(1)} KB · {extOf(file.name).toUpperCase()}</div>}
            </div>
          </div>
        )}

        {/* Library picker */}
        {source === "library" && (
          <div data-testid="pd-library-picker">
            <div className="flex items-center justify-between mb-2">
              <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#666]">
                [ SAVED PDS ]
              </div>
              <button
                type="button"
                onClick={loadSavedPds}
                disabled={pdsLoading || busy}
                className="cal-btn cal-btn-ghost text-xs !py-1 !px-2"
                data-testid="pd-library-refresh"
                aria-label="Refresh saved PDs"
              >
                <RefreshCw size={12} className={pdsLoading ? "animate-spin" : ""} />
              </button>
            </div>
            {pdsLoading ? (
              <div className="text-sm text-[#666]">Loading your saved PDs…</div>
            ) : savedPds.length === 0 ? (
              <div className="cal-card p-6 bg-[#F4F4F0] text-sm" data-testid="pd-library-empty">
                No items in the Library yet.{" "}
                <Link to="/pd-generator" className="underline font-semibold">Draft one with the PD Generator</Link>{" "}
                and it&apos;ll appear here.
              </div>
            ) : (
              <ul className="space-y-2 max-h-72 overflow-y-auto pr-1" data-testid="pd-library-picker-list">
                {savedPds.map((d) => {
                  const active = pickedPdId === d.id;
                  return (
                    <li key={d.id}>
                      <button
                        type="button"
                        onClick={() => setPickedPdId(d.id)}
                        className={`w-full text-left p-3 border transition flex items-start gap-3 ${active ? "bg-[#111] text-white border-[#111]" : "bg-white border-[#DDD] hover:border-[#111]"}`}
                        data-testid={`pd-library-pick-${d.id}`}
                        disabled={busy}
                      >
                        <div className={`mt-0.5 flex-none w-5 h-5 border-2 flex items-center justify-center ${active ? "border-white bg-white text-[#111]" : "border-[#111]"}`}>
                          {active && <Check size={12} />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-display font-semibold text-sm truncate">{d.job_title}</div>
                          <div className={`text-xs truncate ${active ? "text-[#DDD]" : "text-[#666]"}`}>{d.department}</div>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
            {pickedPd && (
              <div className="mt-2 text-xs font-mono-cal text-[#666]" data-testid="pd-library-picked">
                Selected: <b>{pickedPd.job_title}</b>
              </div>
            )}
          </div>
        )}

        {busy && (
          <div className="cal-card p-4 bg-[#F4F4F0]" data-testid="progress">
            {STAGES.map((s, i) => (
              <div key={s} className={`font-mono-cal text-xs my-1 ${i <= stage ? "text-[#111]" : "text-[#AAA]"}`}>
                [{i <= stage ? "✓" : i === stage + 1 ? "…" : " "}] {s}
              </div>
            ))}
          </div>
        )}
        <div className="flex gap-3">
          <button type="button" onClick={() => nav("/dashboard")} className="cal-btn cal-btn-ghost" data-testid="cancel-btn">Cancel</button>
          <button disabled={busy} className="cal-btn flex-1" data-testid="submit-new-profile">
            {busy ? "Processing…" : "Create profile →"}
          </button>
        </div>
      </form>
    </div>
  );
}
